import { motion } from 'framer-motion';
import { useState } from 'react';
import { Twitter, MessageCircle, Github, Send } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

const footerLinks = {
  Product: [
    { name: 'Polk Chain', href: '#' },
    { name: 'Polk Wallet', href: '#' },
    { name: 'Polk Swap', href: '#' },
    { name: 'DApps', href: '#' },
  ],
  Resources: [
    { name: 'Documentation', href: '#' },
    { name: 'Whitepaper', href: '#' },
    { name: 'Blog', href: '#' },
    { name: 'Roadmap', href: '#roadmap' },
  ],
  Company: [
    { name: 'About', href: '#about' },
    { name: 'Team', href: '#team' },
    { name: 'Careers', href: '#' },
    { name: 'Contact', href: '#' },
  ],
  Legal: [
    { name: 'Privacy Policy', href: '#' },
    { name: 'Terms of Service', href: '#' },
    { name: 'Cookie Policy', href: '#' },
  ],
};

const socialLinks = [
  { name: 'Twitter', icon: Twitter, href: '#' },
  { name: 'Telegram', icon: Send, href: '#' },
  { name: 'Discord', icon: MessageCircle, href: '#' },
  { name: 'GitHub', icon: Github, href: '#' },
];

export const Footer = () => {
  const [email, setEmail] = useState('');
  const { isDark } = useTheme();

  const handleSubmit = (e) => {
    e.preventDefault();
    setEmail('');
  };

  return (
    <footer
      className={`py-16 border-t ${
        isDark 
          ? 'bg-[#030303] border-white/5' 
          : 'bg-white border-black/10'
      }`}
      data-testid="footer"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-6 gap-12 mb-12">
          {/* Brand & Newsletter */}
          <div className="lg:col-span-2">
            {/* Logo */}
            <motion.a
              href="#"
              className="flex items-center gap-3 mb-6"
              whileHover={{ scale: 1.02 }}
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_polk-blockchain-next/artifacts/d0rayuzo_Polk%20Chain%20logo%20in%20glowing%20gradient%20design.png" 
                alt="Polk Chain Logo"
                className="h-36 w-auto"
              />
            </motion.a>

            <p className={`mb-6 max-w-sm ${isDark ? 'text-zinc-400' : 'text-slate-600'}`}>
              Building the next generation of decentralized infrastructure for a borderless financial future.
            </p>

            {/* Newsletter */}
            <form onSubmit={handleSubmit} className="space-y-3">
              <label className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Subscribe to our newsletter
              </label>
              <div className="flex gap-2">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  className={`flex-1 px-4 py-3 rounded-full transition-all focus:ring-2 focus:ring-blue-500/50 focus:outline-none ${
                    isDark 
                      ? 'bg-white/5 border border-white/10 text-white placeholder:text-zinc-500' 
                      : 'bg-black/5 border border-black/10 text-gray-900 placeholder:text-slate-400'
                  }`}
                  data-testid="newsletter-email-input"
                />
                <motion.button
                  type="submit"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="px-6 py-3 rounded-full bg-blue-600 hover:bg-blue-500 text-white font-semibold transition-all"
                  data-testid="newsletter-submit-btn"
                >
                  Subscribe
                </motion.button>
              </div>
            </form>
          </div>

          {/* Links */}
          {Object.entries(footerLinks).map(([category, links]) => (
            <div key={category}>
              <h3 className={`font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {category}
              </h3>
              <ul className="space-y-3">
                {links.map((link) => (
                  <li key={link.name}>
                    <a
                      href={link.href}
                      className={`transition-colors hover:text-blue-500 ${
                        isDark ? 'text-zinc-400' : 'text-slate-600'
                      }`}
                    >
                      {link.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom Bar */}
        <div className={`pt-8 border-t flex flex-col sm:flex-row items-center justify-between gap-6 ${
          isDark ? 'border-white/10' : 'border-black/10'
        }`}>
          <p className={`text-sm ${isDark ? 'text-zinc-500' : 'text-slate-500'}`}>
            © 2026 Polk Chain. All rights reserved.
          </p>

          {/* Social Links */}
          <div className="flex items-center gap-4">
            {socialLinks.map((social) => (
              <motion.a
                key={social.name}
                href={social.href}
                whileHover={{ scale: 1.1, y: -2 }}
                whileTap={{ scale: 0.9 }}
                className={`p-2 rounded-lg transition-colors ${
                  isDark 
                    ? 'bg-white/5 hover:bg-white/10' 
                    : 'bg-black/5 hover:bg-black/10'
                }`}
                data-testid={`social-${social.name.toLowerCase()}`}
              >
                <social.icon className={`w-5 h-5 ${isDark ? 'text-zinc-400' : 'text-slate-600'}`} />
              </motion.a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};
